// static/table_engine_src/format/format_numbers.js
import { stripZeros } from "../util/number.js";

const GROUP_SPACE_RE = /[\s\u00A0\u202F_']/g;
const UNICODE_MINUS_RE = /[\u2010-\u2015\u2212\uFE63\uFF0D]/g;
const CURRENCY_EDGE_RE = /^[\$£€¥₹]\s*|\s*[\$£€¥₹]$/g;

function escRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeAltDecimalCandidate(s, decimal, group) {
  if (decimal !== "." && decimal !== ",") return { value: s, used: false };

  const alt = decimal === "." ? "," : ".";
  if (group !== alt) return { value: s, used: false };
  if (!s || s.includes(decimal)) return { value: s, used: false };

  try {
    const altRe = new RegExp(escRe(alt), "g");
    const hits = s.match(altRe);
    if (!hits || hits.length !== 1) return { value: s, used: false };

    const groupedRe = new RegExp(
      `^[+-]?\\d{1,3}(?:${escRe(group)}\\d{3})+(?:e[+-]?\\d+)?$`,
      "i",
    );
    if (groupedRe.test(s)) return { value: s, used: false };

    const decimalRe = new RegExp(
      `^[+-]?(?:\\d+${escRe(alt)}\\d+|${escRe(alt)}\\d+)(?:e[+-]?\\d+)?$`,
      "i",
    );
    if (!decimalRe.test(s)) return { value: s, used: false };

    return { value: s.replace(altRe, "."), used: true };
  } catch {
    return { value: s, used: false };
  }
}

export function resolveNumberSeparators(fmt) {
  let decimal = ".";
  let group = ",";

  const locale =
    fmt && typeof fmt.locale === "string" && fmt.locale.trim() !== ""
      ? fmt.locale.trim()
      : "";

  if (locale && typeof Intl !== "undefined" && typeof Intl.NumberFormat === "function") {
    try {
      const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
      const d = parts.find((p) => p.type === "decimal")?.value;
      const g = parts.find((p) => p.type === "group")?.value;
      if (typeof d === "string" && d) decimal = d;
      if (typeof g === "string" && g) group = g;
    } catch {
      // fallback to defaults
    }
  }

  const decimalOv =
    (typeof fmt?.decimalSep === "string" && fmt.decimalSep) ||
    (typeof fmt?.decimal === "string" && fmt.decimal) ||
    "";
  if (decimalOv) decimal = decimalOv;

  const groupOv =
    (typeof fmt?.groupSep === "string" && fmt.groupSep) ||
    (typeof fmt?.thousandSep === "string" && fmt.thousandSep) ||
    (typeof fmt?.thousandsSep === "string" && fmt.thousandsSep) ||
    "";
  if (groupOv) group = groupOv;

  if (decimal === group) {
    group = group === "," ? "." : ",";
  }

  return { decimal, group };
}

function groupThousands(s, groupSep = ",", decimalSep = ".") {
  const txt = String(s ?? "");
  if (!txt) return txt;

  const sign = txt.startsWith("-") ? "-" : (txt.startsWith("+") ? "+" : "");
  const body = sign ? txt.slice(1) : txt;
  const dot = body.indexOf(".");
  const intPart = dot >= 0 ? body.slice(0, dot) : body;
  const fracPart = dot >= 0 ? `${decimalSep}${body.slice(dot + 1)}` : "";
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);

  return `${sign}${grouped}${fracPart}`;
}

function resolveDp(fmt) {
  const dpRaw = parseNumber(fmt?.dp, NaN);
  if (!Number.isFinite(dpRaw)) return 4;
  return Math.max(0, Math.min(12, Math.trunc(dpRaw)));
}

function normalizeNumberish(value, fmt = null) {
  if (value === null || value === undefined) return "";
  let s = String(value).trim();
  if (s === "") return "";

  s = s.replace(UNICODE_MINUS_RE, "-");

  let parenNegative = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    parenNegative = true;
    s = s.slice(1, -1).trim();
  }

  s = s.replace(CURRENCY_EDGE_RE, "");
  s = s.replace(GROUP_SPACE_RE, "");

  if (fmt && typeof fmt === "object") {
    const { decimal, group } = resolveNumberSeparators(fmt);
    const alt = normalizeAltDecimalCandidate(s, decimal, group);
    if (alt.used) {
      s = alt.value;
    } else {
      if (group && group !== " ") {
        try {
          s = s.replace(new RegExp(escRe(group), "g"), "");
        } catch {
          // keep parsing with generic fallback logic below
        }
      }
      if (decimal && decimal !== ".") {
        try {
          const re = new RegExp(escRe(decimal), "g");
          const hits = s.match(re);
          if (hits && hits.length > 1) return "";
          s = s.replace(re, ".");
        } catch {
          // keep parsing with generic fallback logic below
        }
      }
    }
  }

  let sign = "";
  if (s[0] === "+" || s[0] === "-") {
    sign = s[0];
    s = s.slice(1);
  }
  if (s.includes("+") || s.includes("-")) return "";

  const hasComma = s.includes(",");
  const dotCount = (s.match(/\./g) || []).length;
  const commaCount = (s.match(/,/g) || []).length;

  if (hasComma && dotCount > 0) {
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      s = s.replace(/\./g, "");
      s = s.replace(/,/g, ".");
    } else {
      s = s.replace(/,/g, "");
    }
  } else if (hasComma) {
    const isGrouped = /^\d{1,3}(,\d{3})+(e[+-]?\d+)?$/i.test(s);
    const isDecimalComma = commaCount === 1 && /^\d+,\d+(e[+-]?\d+)?$/i.test(s);

    if (isGrouped) s = s.replace(/,/g, "");
    else if (isDecimalComma) s = s.replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (dotCount > 1) {
    const isDotGrouped = /^\d{1,3}(\.\d{3})+(e[+-]?\d+)?$/i.test(s);
    if (isDotGrouped) s = s.replace(/\./g, "");
  }

  if (parenNegative) {
    if (sign === "-") sign = "";
    else sign = "-";
  }

  const normalized = `${sign}${s}`;
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(normalized)) return "";

  return normalized;
}

export function parseNumber(value, fallback = NaN, fmt = null) {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;

  const normalized = normalizeNumberish(value, fmt);
  if (!normalized) return fallback;

  const n = Number(normalized);
  return Number.isFinite(n) ? n : fallback;
}

export function formatNumber(value, fmt) {
  const x = parseNumber(value, NaN);
  if (!Number.isFinite(x)) return "";

  const seps = resolveNumberSeparators(fmt);
  const group = !!(fmt?.group || fmt?.thousands);
  if (fmt?.int) {
    const out = String(Math.round(x));
    return group ? groupThousands(out, seps.group, seps.decimal) : out;
  }

  const dp = resolveDp(fmt);
  const sci = fmt?.sci === true || fmt?.scientific === true;
  if (sci) {
    let out = x.toExponential(dp);
    if (fmt?.strip) {
      // Trim trailing zeros in mantissa but keep exponent part.
      out = out
        .replace(/(\.\d*?[1-9])0+(e[+-]?\d+)$/i, "$1$2")
        .replace(/\.0+(e[+-]?\d+)$/i, "$1");
    }
    if (seps.decimal !== ".") {
      const eIdx = out.search(/[eE]/);
      if (eIdx > 0) {
        const head = out.slice(0, eIdx).replace(".", seps.decimal);
        return `${head}${out.slice(eIdx)}`;
      }
    }
    return out;
  }

  const s0 = x.toFixed(dp);
  const out = fmt?.strip ? stripZeros(s0) : s0;
  if (group) return groupThousands(out, seps.group, seps.decimal);
  if (seps.decimal !== ".") return out.replace(".", seps.decimal);
  return out;
}

export function roundNumber(value, fmt) {
  const x = parseNumber(value, NaN);
  if (!Number.isFinite(x)) return NaN;
  if (fmt?.int) return Math.round(x);

  const dp = resolveDp(fmt);
  return Number(x.toFixed(dp));
}
